/**
 * Creating a family member, for the onboarding "add a child" step (#576 AD-04).
 *
 * The endpoint is NOT new. `POST /api/admin/users` (`server/routes/admin.routes.js:49`) is the same
 * one the production app calls from `src/pages/AdminUsers.js`, guarded by the admin middleware the
 * rest of `/api/admin` carries. Nothing on the server changes for this; only the redesign gains a
 * way in.
 *
 * **Why there is no `mutationId` here, and what stands in for one.** `tenant_quest_packs` carries a
 * `last_mutation_id` column (`server/services/quest-packs.service.js:364`), so a pack toggle can
 * replay its own write and be answered with the same row. The `users` table carries no such column,
 * and inventing one would be a migration on production data for a development feature. It is not
 * needed: `createUserByAdmin` already selects on `tenant_id` + `LOWER(username)` and throws **409**
 * when a row is there (`server/services/admin.service.js`, the username check ~line 1121). The
 * natural key does the work a mutation id would do — the SECOND identical write cannot create a
 * second child.
 *
 * So the replay rule lives here, and it is deliberately narrow: a 409 is reported as
 * `MEMBER_EXISTS`, and the CALLER decides what that means. A caller replaying its own write treats
 * it as success; a caller sending a new child treats it as "that username is taken". Reporting
 * every 409 as "already added" would tell a family who typed a second Sam that their second Sam was
 * saved, and no child would exist.
 */
import { ApiError, api } from './client.js';

/** The role every admin-created account gets. `server/services/admin.service.js` binds the literal. */
export const MEMBER_ROLE = 'user';

/** What a 409 on the username check is called here, so no caller reads a status code. */
export const MEMBER_EXISTS = 'MEMBER_EXISTS';

/** What any other fault is called. Kept apart so a caller cannot show them in the same sentence. */
export const MEMBER_FAULT = 'MEMBER_FAULT';

/**
 * Suggest a username from the child's name — the SUGGESTION half of "editable suggestions".
 *
 * It is a suggestion and nothing more: the field stays editable, because two children called Sam
 * cannot both be `sam` and only the family knows which one is `sammy`. Lowercase ASCII letters and
 * digits survive; everything else is dropped, which keeps a name with an accent or a space from
 * producing a username the server would refuse.
 *
 * @param {string} name the child's display name, as typed
 * @returns {string} a suggested username, possibly empty when the name carries no usable character
 */
export function suggestUsername(name) {
  if (typeof name !== 'string') return '';
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 30);
}

/**
 * Name the failure behind a thrown error, without any caller reading a status code.
 *
 * @param {unknown} error whatever `createFamilyChild` threw
 * @returns {string} `MEMBER_EXISTS` for the username conflict, `MEMBER_FAULT` for everything else
 */
export function memberErrorCode(error) {
  if (error instanceof ApiError && error.status === 409) return MEMBER_EXISTS;
  return MEMBER_FAULT;
}

/**
 * What the server's 409 says about the sign-in name, for the "pick a free one" half (#580).
 *
 * Since d-20261002-10 a sign-in name belongs to ONE account across every family. A 409 whose `code`
 * is `SIGN_IN_NAME_TAKEN` means ANOTHER family holds it — never which one; the server does not say.
 * Either 409 may carry `suggestions` the server checked are free everywhere
 * (`server/services/admin.service.js` `generateUsernameSuggestions`). Only one that the server's own
 * rule would accept (3-30 letters, digits, `_` or `-`) is offered, so a bad body cannot put a name in
 * the field that the next press would have refused.
 *
 * @param {unknown} error whatever `createFamilyChild` threw
 * @returns {{ elsewhere: boolean, suggestion: string|null }}
 */
export function memberConflictDetail(error) {
  const body = error instanceof ApiError && error.status === 409 ? error.body : null;
  const suggestions = Array.isArray(body?.suggestions) ? body.suggestions : [];
  const suggestion = suggestions.find((s) => typeof s === 'string' && /^[a-zA-Z0-9_-]{3,30}$/.test(s)) ?? null;
  return { elsewhere: body?.code === 'SIGN_IN_NAME_TAKEN', suggestion };
}

/* --------------------------------------------- the child's first password (#576 AD-04) */

/**
 * The alphabets the generated password draws from.
 *
 * Deliberately missing: `O` and `0`, `I` and `l` and `1`. A parent writes this password on a scrap
 * of paper and types it on a child's tablet, and the pair a person cannot tell apart is the pair
 * that turns into a support request.
 */
const PASSWORD_UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const PASSWORD_LOWER = 'abcdefghijkmnpqrstuvwxyz';
const PASSWORD_DIGIT = '23456789';
const PASSWORD_LENGTH = 14;

/** One index below `max`, drawn from the platform's cryptographic source rather than `Math.random`. */
function randomIndex(max) {
  const webCrypto = globalThis.crypto;
  if (!webCrypto || typeof webCrypto.getRandomValues !== 'function') {
    throw new Error('this browser cannot generate a password (crypto.getRandomValues is missing)');
  }
  // Rejection sampling, so a value is never biased toward the low indices by a plain modulo.
  const limit = Math.floor(0xffffffff / max) * max;
  const buffer = new Uint32Array(1);
  let draw;
  do {
    webCrypto.getRandomValues(buffer);
    draw = buffer[0];
  } while (draw >= limit);
  return draw % max;
}

/**
 * Generate the child's first password.
 *
 * **Why the client generates one at all.** `createUserByAdmin` branches on `isEmailUsername`
 * (`server/services/admin.service.js`, the password section). An email account gets an
 * auto-generated password and a set-your-own link. A child has no email address, so the caller MUST
 * supply a password — and without one the server answers **400 "Password is required for non-email
 * users"**. That 400 is what a live browser run hit on dev after PR #624, with every unit test
 * green, because they all mocked this call.
 *
 * **Why not ask the parent for one.** The redesign's intended child sign-in is a PIN on a
 * remembered device, and that backend does not exist yet (the same gap that blocks #576 o1, o10 and
 * o14). Until it does, a generated password the parent is SHOWN and told to keep is the honest
 * shape: it is the same thing `src/pages/AdminUsers.js` does in the production app, which offers a
 * generate button and reveals the created password once.
 *
 * The classes are guaranteed by construction rather than by retrying a random draw, so this cannot
 * loop. `src/api/familyMembers.initialPassword.test.js` checks every draw against the server's OWN
 * `validatePasswordStrength`, so a change to that rule fails here rather than on a family's screen.
 *
 * @returns {string} a password of `PASSWORD_LENGTH` characters carrying an uppercase letter, a
 *                   lowercase letter and a digit
 */
export function generateInitialPassword() {
  const pool = PASSWORD_UPPER + PASSWORD_LOWER + PASSWORD_DIGIT;
  const chars = [
    PASSWORD_UPPER[randomIndex(PASSWORD_UPPER.length)],
    PASSWORD_LOWER[randomIndex(PASSWORD_LOWER.length)],
    PASSWORD_DIGIT[randomIndex(PASSWORD_DIGIT.length)],
  ];
  while (chars.length < PASSWORD_LENGTH) chars.push(pool[randomIndex(pool.length)]);
  // Fisher-Yates, so the guaranteed three do not always sit in the first three positions.
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = randomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

/**
 * Create one child on the family's account.
 *
 * `birthDate` is REQUIRED by the server and refused as a 400 when absent
 * (`server/services/admin.service.js:1013`), so it is required here rather than defaulted — a
 * default birthday would be invented data about a real child.
 *
 * `isEmailUsername` is always `false`: a child signs in by username, and sending an email address
 * as the username would make the account reachable by email without a verified address.
 *
 * `initialPassword` is REQUIRED and is checked here, before the request leaves the browser. Letting
 * a caller omit it would earn a 400 the family reads as "we could not add that child" with nothing
 * they can do about it — which is exactly what shipped in PR #624 and had to be fixed the same day.
 *
 * @param {object} child
 * @param {string} child.displayName what the family calls this child, 2 to 100 characters
 * @param {string} child.username the sign-in name, unique across every family (#580, d-20261002-10)
 * @param {string} child.birthDate `YYYY-MM-DD`
 * @param {string} child.initialPassword the child's first password; see `generateInitialPassword`
 * @returns {Promise<object>} the created member row, as the server returned it
 */
export async function createFamilyChild({ displayName, username, birthDate, initialPassword }) {
  if (typeof initialPassword !== 'string' || initialPassword === '') {
    throw new Error('a child account needs an initial password (the server refuses one without it)');
  }
  const answer = await api.post('/api/admin/users', {
    username,
    isEmailUsername: false,
    displayName,
    birthDate,
    initialPassword,
  });
  return answer?.user ?? null;
}
